import { type ReactNode, useState } from 'react';
import {
  Box,
  Divider,
  Flex,
  Menu,
  ScrollArea,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { IconChevronDown, IconSearch } from '@tabler/icons-react';
import type { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import type { TemplateWithoutSections } from 'src/data-capture/hooks/templates/useTemplates';
import { Button } from 'src/design-system/Button';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Props = {
  templates: TemplateWithoutSections[];
  defaultTemplateId?: string;
  agendaFiles: UploadedFile[];
  defaultAgendaFileId?: string;
  isGenerating: boolean;
  onGenerateTemplate: (templateId: string, summarise: boolean) => void;
  onGenerateAgenda: (agendaFileId: string, summarise: boolean) => void;
};

const switchStyles = { track: { width: 28, minWidth: 28, height: 16 } };

const DROPDOWN_MAX_HEIGHT = 240;
const SEARCH_MIN_OPTIONS = 6;

export const StructureSelection = ({
  templates,
  defaultTemplateId,
  agendaFiles,
  defaultAgendaFileId,
  isGenerating,
  onGenerateTemplate,
  onGenerateAgenda,
}: Props) => {
  const [templateId, setTemplateId] = useState(defaultTemplateId ?? templates[0]?.id);
  const [templateSummarise, setTemplateSummarise] = useState(true);
  const [agendaFileId, setAgendaFileId] = useState(defaultAgendaFileId ?? agendaFiles[0]?.id);
  const [agendaSummarise, setAgendaSummarise] = useState(true);
  const [generatingOption, setGeneratingOption] = useState<'template' | 'agenda'>();

  const selectedTemplate = templates.find(template => template.id === templateId);
  const selectedAgenda = agendaFiles.find(file => file.id === agendaFileId);

  return (
    <Stack gap={0}>
      <Text size="lg" fw={FONT_WEIGHTS.semibold} c="black">
        How would you like to structure the meeting note?
      </Text>

      <Stack gap="xs" mt="sm">
        <OptionBox
          title="Use a template"
          subtitle="Generate meeting note using a predefined template"
          isGenerating={isGenerating && generatingOption === 'template'}
          generateDisabled={!templateId || isGenerating}
          onGenerate={() => {
            if (!templateId) return;
            setGeneratingOption('template');
            onGenerateTemplate(templateId, templateSummarise);
          }}
          summarise={templateSummarise}
          onSummariseChange={setTemplateSummarise}
          dropdownLabel="Template"
          dropdownValue={selectedTemplate?.displayName}
          options={templates.map(template => ({ id: template.id, label: template.displayName }))}
          onSelectOption={setTemplateId}
        />

        <OptionBox
          title="Use an agenda"
          subtitle="Generate meeting note using an agenda file as the structure"
          isGenerating={isGenerating && generatingOption === 'agenda'}
          generateDisabled={!agendaFileId || isGenerating}
          onGenerate={() => {
            if (!agendaFileId) return;
            setGeneratingOption('agenda');
            onGenerateAgenda(agendaFileId, agendaSummarise);
          }}
          summarise={agendaSummarise}
          onSummariseChange={setAgendaSummarise}
          dropdownLabel="Agenda"
          dropdownValue={selectedAgenda?.file.fileName}
          options={agendaFiles.map(file => ({ id: file.id, label: file.file.fileName }))}
          onSelectOption={setAgendaFileId}
        />
      </Stack>
    </Stack>
  );
};

type OptionBoxProps = {
  title: string;
  subtitle: string;
  isGenerating: boolean;
  generateDisabled: boolean;
  onGenerate: () => void;
  summarise: boolean;
  onSummariseChange: (value: boolean) => void;
  dropdownLabel: string;
  dropdownValue?: string;
  options: { id: string; label: string }[];
  onSelectOption: (id: string) => void;
};

function OptionBox({
  title,
  subtitle,
  isGenerating,
  generateDisabled,
  onGenerate,
  summarise,
  onSummariseChange,
  dropdownLabel,
  dropdownValue,
  options,
  onSelectOption,
}: OptionBoxProps) {
  const [search, setSearch] = useState('');

  const query = search.trim().toLowerCase();
  const filteredOptions = query
    ? options.filter(option => option.label.toLowerCase().includes(query))
    : options;
  const withSearch = options.length >= SEARCH_MIN_OPTIONS;
  const optionNoun = dropdownLabel.toLowerCase();

  return (
    <Box bg="gray.0" p="sm" style={{ borderRadius: 12 }}>
      <Flex justify="space-between" align="center" gap="sm">
        <Box style={{ minWidth: 0 }}>
          <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
            {title}
          </Text>
          <Text size="xs" fw={FONT_WEIGHTS.regular} c="gray.7" mt={2}>
            {subtitle}
          </Text>
        </Box>
        <Button
          variant="primary"
          size="sm"
          onClick={onGenerate}
          loading={isGenerating}
          disabled={generateDisabled}
          style={{ flexShrink: 0 }}
        >
          Generate
        </Button>
      </Flex>

      <Divider color="gray.1" mt="xxs" />

      <Flex gap="xxs" mt="xxs">
        <SelectorBox>
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
            Summarise meeting note
          </Text>
          <Switch
            checked={summarise}
            onChange={event => onSummariseChange(event.currentTarget.checked)}
            color="brand.6"
            styles={switchStyles}
          />
        </SelectorBox>

        <SelectorBox>
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
            {dropdownLabel}
          </Text>
          <Menu
            position="bottom-end"
            width={260}
            withinPortal
            // Mantine autofocuses its own placeholder element first, which would leave the
            // search box needing a second click before you can type in it.
            withInitialFocusPlaceholder={!withSearch}
            // Resetting on `onClose` would flash the unfiltered list back in while the
            // dropdown is still fading out.
            onExitTransitionEnd={() => setSearch('')}
            styles={{ itemLabel: { minWidth: 0, overflowWrap: 'anywhere' } }}
          >
            <Menu.Target>
              <Flex align="center" gap="xxxs" style={{ cursor: 'pointer', minWidth: 0 }}>
                <Text
                  size="sm"
                  fw={FONT_WEIGHTS.medium}
                  c="gray.7"
                  lineClamp={1}
                  style={{ maxWidth: 120 }}
                >
                  {dropdownValue ?? 'Select'}
                </Text>
                <IconChevronDown
                  size={16}
                  color="var(--mantine-color-gray-10)"
                  style={{ flexShrink: 0 }}
                />
              </Flex>
            </Menu.Target>
            <Menu.Dropdown>
              {withSearch && (
                <TextInput
                  data-autofocus
                  value={search}
                  onChange={event => setSearch(event.currentTarget.value)}
                  placeholder={`Search ${optionNoun}s`}
                  aria-label={`Search ${optionNoun}s`}
                  leftSection={<IconSearch size={14} />}
                  size="xs"
                  mb="xxs"
                />
              )}
              {options.length === 0 && <Menu.Item disabled>No options available</Menu.Item>}
              {options.length > 0 && filteredOptions.length === 0 && (
                <Menu.Item disabled>No matching {optionNoun}s</Menu.Item>
              )}
              <ScrollArea.Autosize mah={DROPDOWN_MAX_HEIGHT} type="auto">
                {filteredOptions.map(option => (
                  <Menu.Item key={option.id} onClick={() => onSelectOption(option.id)}>
                    {option.label}
                  </Menu.Item>
                ))}
              </ScrollArea.Autosize>
            </Menu.Dropdown>
          </Menu>
        </SelectorBox>
      </Flex>
    </Box>
  );
}

function SelectorBox({ children }: { children: ReactNode }) {
  return (
    <Flex
      flex={1}
      justify="space-between"
      align="center"
      gap="sm"
      bg="white"
      px="sm"
      py="xs"
      style={{ borderRadius: 16, minWidth: 0 }}
    >
      {children}
    </Flex>
  );
}
